import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { bookmarksApi } from "marketplace/bookmarks/_api/bookmark-api";
import {
  getErrorLoadingIndividualBookmarksByQuery,
  getFailedIndividualBookmarkPageByQuery,
  getIndividualBookmarksByQuery,
  getIsLoadingIndividualBookmarksByQuery,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import type IndividualBookmarkQuery from "marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

export const INDIVIDUAL_BOOKMARK_PAGE_SIZE = 25;

type UseInfiniteIndividualBookmarksOptions = {
  skip?: boolean;
};

/**
 * Loads the caller's individual bookmarks one page at a time, for a list that loads more when it
 * scrolls to its end. A new search or order starts again from the first page.
 *
 * The first page stays subscribed while the list shows this search. A bookmark created or removed
 * anywhere invalidates it, and its refetch replaces the pages held, so the list never keeps
 * showing what the server no longer says. A later page is released once it arrives: the slice
 * holds it, and the refreshed first page drops it.
 */
export default function useInfiniteIndividualBookmarks(
  query: IndividualBookmarkQuery,
  options?: UseInfiniteIndividualBookmarksOptions,
) {
  const skip = options?.skip ?? false;
  const dispatch = useAttorneyHubDispatch();
  const bookmarks = useSelector(getIndividualBookmarksByQuery(query));
  const isLoading = useSelector(getIsLoadingIndividualBookmarksByQuery(query));
  const error = useSelector(getErrorLoadingIndividualBookmarksByQuery(query));
  const failedPage = useSelector(getFailedIndividualBookmarkPageByQuery(query));
  const requestsRef = useRef(new Set<{ abort?: () => void; unsubscribe?: () => void }>());
  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const dispatchPage = useCallback(
    (pageIndex: number, append: boolean, keepSubscribed = false) => {
      const pageQuery = queryRef.current.withPagination(
        PaginationParameters.create({ pageIndex, pageSize: INDIVIDUAL_BOOKMARK_PAGE_SIZE }),
      );
      // RTK Query keeps an answer for the same arguments, and would not ask again. The slice
      // holds the pages, so each ask must reach the server.
      const request = dispatch(
        bookmarksApi.endpoints.getIndividualBookmarks.initiate(
          { query: pageQuery, append },
          { forceRefetch: true },
        ),
      );
      if (keepSubscribed) return request;
      requestsRef.current.add(request);
      void request.finally(() => {
        requestsRef.current.delete(request);
        request.unsubscribe?.();
      });
      return request;
    },
    [dispatch],
  );

  useEffect(() => {
    if (skip) return;
    const request = dispatchPage(0, false, true);
    return () => {
      // A dialog can re-run this effect as it settles after opening. Do not cancel the
      // initial page in that cleanup: a replacement subscriber can use the same request.
      request.unsubscribe?.();
    };
  }, [dispatchPage, query.cacheKey, skip]);

  useEffect(
    () => () => {
      for (const request of requestsRef.current) {
        // Let RTK Query finish a short list request so a transient dialog unmount does not
        // turn the initial load into an error that requires a manual retry.
        request.unsubscribe?.();
      }
      requestsRef.current.clear();
    },
    [],
  );

  const loadMore = useCallback(() => {
    if (skip || isLoading || !bookmarks.hasNextPage) return;
    dispatchPage(bookmarks.pageIndex + 1, true);
  }, [bookmarks.hasNextPage, bookmarks.pageIndex, dispatchPage, isLoading, skip]);

  // Asks again for the page that failed, as it was asked: a failed refresh of the first page
  // replaces the pages held, and a failed later page appends. With no failure, the first page.
  const retry = useCallback(() => {
    if (skip || isLoading) return;
    dispatchPage(failedPage?.pageIndex ?? 0, failedPage?.append ?? false);
  }, [dispatchPage, failedPage, isLoading, skip]);

  return useMemo(
    () => ({
      bookmarks,
      isLoading,
      isInitialLoading: isLoading && bookmarks.isEmpty,
      error,
      loadMore,
      retry,
    }),
    [bookmarks, error, isLoading, loadMore, retry],
  );
}
